import { cloneElement, isValidElement, useEffect, useId, useRef, useState } from 'react';

const POSITION_CLASSES = {
  top: 'bottom-full left-1/2 mb-2 -translate-x-1/2',
  bottom: 'left-1/2 top-full mt-2 -translate-x-1/2',
  left: 'right-full top-1/2 mr-2 -translate-y-1/2',
  right: 'left-full top-1/2 ml-2 -translate-y-1/2',
};

const POSITION_OPPOSITES = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

const FALLBACK_ORDER = ['top', 'bottom', 'right', 'left'];

export function resolveTooltipPosition(preferredPosition, anchorRect, tooltipRect, viewport) {
  const preferred =
    preferredPosition === 'auto'
      ? 'auto'
      : POSITION_CLASSES[preferredPosition]
      ? preferredPosition
      : 'top';
  const spaces = {
    top: anchorRect.top,
    bottom: viewport.height - anchorRect.bottom,
    left: anchorRect.left,
    right: viewport.width - anchorRect.right,
  };
  const requiredSpaces = {
    top: tooltipRect.height + 12,
    bottom: tooltipRect.height + 12,
    left: tooltipRect.width + 12,
    right: tooltipRect.width + 12,
  };

  const fits = (side) => spaces[side] >= requiredSpaces[side];

  if (preferred === 'auto') {
    return FALLBACK_ORDER.reduce((bestSide, side) => {
      if (!bestSide) return side;
      return spaces[side] > spaces[bestSide] ? side : bestSide;
    }, null);
  }

  if (fits(preferred)) return preferred;

  const opposite = POSITION_OPPOSITES[preferred];
  if (opposite && fits(opposite)) return opposite;

  return FALLBACK_ORDER.reduce((bestSide, side) => {
    if (!bestSide) return side;
    return spaces[side] > spaces[bestSide] ? side : bestSide;
  }, preferred);
}

export default function Tooltip({
  children,
  content,
  position = 'auto',
  wrapperClassName = '',
  tooltipClassName = '',
  contentClassName = '',
}) {
  const tooltipId = useId();
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);
  const [resolvedPosition, setResolvedPosition] = useState(position === 'auto' ? 'top' : position);
  const [isVisible, setIsVisible] = useState(false);
  const child = isValidElement(children)
    ? cloneElement(children, {
        'aria-describedby': [children.props['aria-describedby'], tooltipId].filter(Boolean).join(' ') || undefined,
      })
    : children;

  useEffect(() => {
    function updatePosition() {
      const wrapper = wrapperRef.current;
      const tooltip = tooltipRef.current;
      if (!wrapper || !tooltip) return;

      const anchorRect = wrapper.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const nextPosition = resolveTooltipPosition(position, anchorRect, tooltipRect, {
        width: window.innerWidth,
        height: window.innerHeight,
      });

      setResolvedPosition(nextPosition);
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [position]);

  const placement = POSITION_CLASSES[resolvedPosition] || POSITION_CLASSES.top;

  const showTooltip = () => setIsVisible(true);
  const hideTooltip = () => setIsVisible(false);

  return (
    <span
      ref={wrapperRef}
      className={`relative inline-flex overflow-visible ${wrapperClassName}`.trim()}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {child}
      <span
        ref={tooltipRef}
        id={tooltipId}
        role="tooltip"
        className={`pointer-events-none absolute z-50 max-w-[min(16rem,calc(100vw-1rem))] rounded-lg border border-[var(--dash-border-strong)] bg-[var(--dash-black)] px-3 py-1.5 text-xs font-medium text-white shadow-[0_12px_30px_rgba(0,0,0,0.35)] transition-all duration-150 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'} ${placement} ${tooltipClassName}`.trim()}
      >
        <span className={`whitespace-nowrap ${contentClassName}`.trim()}>{content}</span>
      </span>
    </span>
  );
}
